import { Accordion, AccordionItem } from '@carbon/react';
import { TractNRI } from 'components/nri-table/types';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { workflowStatusAtom } from './atoms';
import { useNRIReviewData, useTractIntersectionRequestAnalysis } from './hooks';
import { NriReviewAllocationManager } from './nri-review-allocation-manager';
import style from './nri-review.module.scss';
import { SlotNRITable } from './nri/slot-nri-table';
import { TractDetails } from './tract-details';
import { SlotSummaryTable } from 'components/slot-summary-table';
import { WorkflowStatusPopover } from './workflow-status-popover';
import { wellStatusCounts } from 'utils/slot-categories';

export const NriReview = () => {
  const [isTractDetailsOpen, setIsTractDetailsOpen] = useState(true);
  const [isNriConfigOpen, setIsNriConfigOpen] = useState(true);
  const { isLoading: isNRILoading, tractNRI } = useNRIReviewData();

  const workflowStatus = useAtomValue(workflowStatusAtom);
  const {
    isLoading,
    currentDevelopmentTractIntersectionAnalysis,
    futureDevelopmentTractIntersectionAnalysis,
  } = useTractIntersectionRequestAnalysis();

  // TODO: use data from current or future development analysis?
  const slotSummary = useMemo(() => {
    const analysis =
      currentDevelopmentTractIntersectionAnalysis ??
      futureDevelopmentTractIntersectionAnalysis;
    return (
      analysis?.slotsSummary?.rows?.map((row) => ({
        total: row.total,
        wellType: row.unitType,
        producing: row.pdp,
        duc: row.duc,
        permit: row.permit,
        ltd: row.ltd,
        shutIn: row.shutIn,
        inactive: row.inactive,
        ...wellStatusCounts(row),
        expiredPermit: row.expiredPermit,
        wellboreExcluded: row.wellboreExcluded ?? 0,
      })) ?? []
    );
  }, [
    currentDevelopmentTractIntersectionAnalysis,
    futureDevelopmentTractIntersectionAnalysis,
  ]);

  const [selectedRow, setSelectedRow] = useState<TractNRI | undefined>(
    undefined
  );

  const handleRowSelected = (row: TractNRI | null) => {
    setSelectedRow(row ?? undefined);
  };

  return (
    <div>
      <h2 className={style.titleContainer}>NRI Review</h2>
      <div>
        <Accordion>
          {/* Section 1: Tract & Subtract Details */}
          <AccordionItem
            className={style.accordionContainer}
            title={
              <div className={style.tractDetailsTitleContainer}>
                <span className={style.tractDetailsTitle}>
                  Tract & Subtract Details
                </span>
                <WorkflowStatusPopover
                  statusItems={workflowStatus.statusItems}
                  overallStatus={workflowStatus.overallStatus}
                />
              </div>
            }
            open={true}
            onHeadingClick={() => setIsTractDetailsOpen(!isTractDetailsOpen)}
          >
            <div className={style.linkedItemSectionContainer}>
              <TractDetails />
            </div>
          </AccordionItem>

          {/* Section 2: Slot Summary */}
          <AccordionItem
            className={style.accordionContainer}
            title={
              <span className={style.tractDetailsTitle}>Slot Summary</span>
            }
            open={true}
          >
            <div className={style.linkedItemSectionContainer}>
              <SlotSummaryTable
                slotSummary={slotSummary}
                isLoading={isLoading}
                tableId="crm-nri-review-slot-summary"
              />
            </div>
          </AccordionItem>

          {/* Section 3: NRI Configuration */}
          <AccordionItem
            title={
              <span className={style.tractDetailsTitle}>NRI Configuration</span>
            }
            className={style.accordionContainer}
            open={true}
            onHeadingClick={() => setIsNriConfigOpen(!isNriConfigOpen)}
          >
            {isNriConfigOpen && (
              <div className={style.nriConfigurationContainer}>
                <div className={style.nriLeftPanel}>
                  {/* Allocation Manager Section */}
                  <NriReviewAllocationManager selectedRow={selectedRow} />

                  {/* Results Table Section - Skeleton */}
                  <div className={style.nriSection}>
                    <div className={style.nriSectionContent}>
                      <SlotNRITable
                        tableId="crm-tract-wizard-slot-table"
                        slotNRIRows={
                          tractNRI?.map((row) => ({
                            ...row,
                            // Keep crmTractSalesforceId for TractNriMatrixCard
                            // Clear it on subtracts to prevent clickable subtract links opening wizard
                            subtracts: row.subtracts?.map((subtract) => ({
                              ...subtract,
                              crmTractSalesforceId: undefined,
                            })),
                          })) ?? []
                        }
                        isLoading={isNRILoading}
                        selectedRow={selectedRow}
                        onRowSelected={handleRowSelected}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};
